/**
 * Registry and shared payload-building for the generic per-investor task-assignment
 * mechanism: an admin can assign a shareholder one or more "task types" (currently just
 * beneficiary designation), each with an optional due date, tracked as a list on the
 * investor's client record rather than a one-off boolean per task. Adding a new task type
 * means adding an entry to ASSIGNABLE_TASK_TYPES, not a new field/eventName pair.
 *
 * assigned_tasks entries: { key: string, dueDate: string|null } -- dueDate is an optional
 * ISO date string (YYYY-MM-DD) an admin sets when assigning; there is no auto-computed
 * "completed" status here (see the shareholder-facing task-queue widget in
 * shared_assets/index.js) -- a task is considered done once an admin removes it from this
 * list, not via any live data check.
 */

/**
 * @typedef {{ key: string; label: string; url: string }} AssignableTaskType
 * @typedef {{ key: string; dueDate: string | null }} AssignedTask
 */

// label is action-oriented, written as an instruction to the investor (e.g. "Complete ..."),
// and shown as-is in both the admin's Assign Task modal/Assigned Tasks list and the investor's
// dashboard widget -- one label per task type, not one per audience.
/** @type {Array<AssignableTaskType>} */
export const ASSIGNABLE_TASK_TYPES = [
	{
		key: 'beneficiary_designation',
		label: 'Complete Beneficiary Designation',
		url: '/beneficiaries/',
	},
];

/**
 * @param {string} key
 * @returns {AssignableTaskType | undefined}
 */
export function getAssignableTaskType(key) {
	return ASSIGNABLE_TASK_TYPES.find((t) => t.key === key);
}

// Tasks a workflow assigns and clears itself (NthWorkflows user-task-v1 / await-signature-v1); never
// offered in the admin's Assign Task list. Each entry carries its run (wf_exec_id) and usually its own
// label; the link opens that run's step (helpers/wf/user-tasks.js).
export const WORKFLOW_TASK_TYPES = [
	{ key: 'sign_document', label: 'Sign a Document', path: '/actions/action/' },
	{ key: 'confirm_83b', label: 'Confirm Your 83(b) Election Filing', path: '/actions/action/' },
	// collection-campaign-v1 (shareholder-tasks-v1): label is 'Respond: <collection title>'.
	{ key: 'collection_response', label: 'Respond to a Request', path: '/actions/action/' },
	// voting-campaign-v1's Vote task: the vote page itself (it finds the active campaign), not a run step.
	{ key: 'cast_vote', label: 'Cast Your Vote', url: '/proxy-voting/' },
];

/** An assignable or workflow task type. */
export function getTaskType(key) {
	return getAssignableTaskType(key) ?? WORKFLOW_TASK_TYPES.find((t) => t.key === key);
}

/** Where a task's Start link goes: a workflow task's run step, else its type's fixed url. */
export function taskUrl(task) {
	// An entry built with its own link (a signer's combined collection task); null means no link (preview).
	if (task && Object.prototype.hasOwnProperty.call(task, 'url')) return task.url;
	const workflowType = WORKFLOW_TASK_TYPES.find((t) => t.key === task?.key);
	if (workflowType?.url) return workflowType.url;
	if (workflowType) return task.wf_exec_id ? `${workflowType.path}?wf_exec_id=${encodeURIComponent(task.wf_exec_id)}` : null;
	return getAssignableTaskType(task?.key)?.url ?? null;
}

/**
 * Normalize one assigned_tasks entry to { key, dueDate }. Tolerates a bare key string (in
 * case any earlier-shape data exists from local testing) by treating it as { key, dueDate: null }.
 *
 * @param {unknown} entry
 * @returns {AssignedTask | null}
 */
function normalizeAssignedTask(entry) {
	if (typeof entry === 'string') return { key: entry, dueDate: null };
	if (entry && typeof entry === 'object' && typeof entry.key === 'string') {
		// Keep any other fields: a workflow task's wf_exec_id/doc_filename must survive a round trip.
		return { ...entry, key: entry.key, dueDate: entry.dueDate || null };
	}
	return null;
}

/**
 * Read the full list of assigned tasks (key + optional due date) for an investor's client
 * record. Accepts the `clients[client]` object directly (works for shareholderClientData, a
 * cap-table row's clients[client], or userData.clients[client]).
 *
 * @param {Record<string, unknown> | undefined | null} clientRecord
 * @returns {Array<AssignedTask>}
 */
export function getAssignedTasks(clientRecord) {
	if (!Array.isArray(clientRecord?.assigned_tasks)) return [];
	return clientRecord.assigned_tasks.map(normalizeAssignedTask).filter(Boolean);
}

/**
 * Read just the task-type keys currently assigned -- for callers that only need to check
 * "is X assigned", not the due date.
 *
 * @param {Record<string, unknown> | undefined | null} clientRecord
 * @returns {Array<string>}
 */
export function getAssignedTaskKeys(clientRecord) {
	return getAssignedTasks(clientRecord).map((t) => t.key);
}

/**
 * @param {Record<string, unknown> | undefined | null} clientRecord
 * @param {string} taskKey
 * @returns {boolean}
 */
export function isTaskAssigned(clientRecord, taskKey) {
	return getAssignedTaskKeys(clientRecord).includes(taskKey);
}

/**
 * @param {Record<string, unknown> | undefined | null} clientRecord
 * @param {string} taskKey
 * @returns {string | null} The task's due date (ISO date string), or null if unassigned/unset.
 */
export function getTaskDueDate(clientRecord, taskKey) {
	const task = getAssignedTasks(clientRecord).find((t) => t.key === taskKey);
	return task ? task.dueDate : null;
}

/**
 * Build the postURL/eventName payload for writing a full replacement assigned_tasks list for
 * one investor. Callers must read the investor's CURRENT list first (getAssignedTasks) and
 * pass the full desired list here -- this does not merge/append itself, since the caller may
 * be adding one task, removing one, or changing a due date.
 *
 * @param {{ client: string; investor: string; userid: string; cookie: string; env: string; assignedTasks: Array<AssignedTask> }} params
 * @returns {Record<string, unknown>}
 */
export function buildTaskAssignmentPayload({ client, investor, userid, cookie, env, assignedTasks }) {
	return {
		eventName: 'admin_user_edit.tasks_assigned',
		clients: {
			[client]: {
				assigned_tasks: assignedTasks.map((t) => ({ ...t, key: t.key, dueDate: t.dueDate || null })),
				lastUpdated: new Date().toISOString(),
			},
		},
		investor,
		userid,
		client,
		cookie,
		'io.nthround.env': env,
	};
}
